import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import styles from './AssumptionsModal.css';
import Modal from '../Modal/Modal';
import GlanceCard from '../GlanceCard/GlanceCard';
import GlanceCardContent from '../GlanceCard/GlanceCardContent';
import withPensionExpertTier from '../../util/pensionExpertTier/withPensionExpertTier';

import arrowDown from '../../assets/images/rebranding/triangle-downward.svg';
import closeIcon from '../../assets/images/rebranding/cross-icon-in-circle.svg';

function AssumptionsModal({ show, onClose, subHeadings, isAdviser }) {
  const [showAssumptions, setShowAssumptions] = useState(false);
  const toggleAssumptions = () => setShowAssumptions((prev) => !prev);

  return (
    <Modal show={show} backDropClick={onClose}>
      <GlanceCard>
        <GlanceCardContent wrapperStyles={{ maxHeight: '85vh' }}>
          <div className={styles.modalHeader}>
            <h3 className={styles.heading}>{'Income estimation'}</h3>
            <div
              className={styles.closeWrapper}
              onClick={() => {
                setShowAssumptions(false);
                onClose();
              }}
              onKeyUp={() => {}}
              role="button"
              tabIndex="0"
            >
              <img src={closeIcon} alt="Close icon" />
              {' '}
              <span className={styles.closeWrapperText}>{'Close'}</span>
            </div>
          </div>
          {subHeadings && (
            <div className={styles.yourPensionPlan}>
              {subHeadings.map((subHeading) => {
                return (
                  <div key={subHeading.title} className={styles.subHeadingWrapper}>
                    <p className={styles.subHeadingTitle}>{subHeading.title}</p>
                    <p className={styles.subHeadingContent}>{subHeading.content}</p>
                  </div>
                );
              })}
            </div>
          )}
          <hr />
          <div className={styles.forecastSection}>
            <h4 className={styles.forecastHeading}>{'Why does the forecast change?'}</h4>
            <ul className={styles.forecastList}>
              {
                [
                  'We periodically update our model to reflect evolving market conditions, investment strategies, and economic landscapes. This ensures that your pension forecast remains aligned with both high and low market scenarios.',
                  'You may notice that your forecast decreases after periods of strong returns - this is to anticipate potential future volatility. Conversely, it may increase when the outlook improves following market declines. These adjustments help provide a balanced and realistic projection of your pension over time. ',
                  'A key part of the service we provide is oversight by our Investment Committee. This committee, made up of experts, carefully reviews market trends, risk factors, and long-term investment strategies. Their expertise helps ensure that your pension remains well-positioned for the future, balancing growth opportunities with risk management to support stable and sustainable outcomes.',
                ].map((text, index) => (
                  <li key={`forecast-item-${index.toString()}`} className={styles.forecastListItem}>
                    <div className={styles.forecastListItemIndex}>{index + 1}</div>
                    <span className={styles.forecastListItemText}>{text}</span>
                  </li>
                ))
              }
              <li className={styles.forecastSupportMessage}>{`As your pension ${isAdviser ? 'advisers' : 'experts'}, we are here to support you through your investment journey. Please send us a message if you have any questions.`}</li>
            </ul>
          </div>
          <div className={styles.assumptionsSection}>
            <button
              type="button"
              aria-expanded={showAssumptions}
              className={styles.assumptionsHeader}
              onClick={toggleAssumptions}
            >
              <h4 className={styles.assumptionsHeading}>{'Assumptions to calculate it'}</h4>
              <img
                className={classNames(styles.assumptionsToggleIcon, {
                  [styles.assumptionsToggleIconOpen]: showAssumptions,
                })}
                src={arrowDown}
                alt="Toggle assumptions section"
              />
            </button>
            {showAssumptions && (
              <ul className={styles.assumptionsUnorderedList} style={{ paddingInlineStart: '30px' }}>
                <li className={styles.assumptionsListItem}>{'The calculator is for illustrative purposes only, and figures may be higher or lower than those shown. It should not be regarded as personal advice.'}</li>
                <li className={styles.assumptionsListItem}>{'The estimated income per year has been calculated using stochastic data to simulate the probability that you would be able to maintain that income for the duration of your retirement, taking into account an expected 50th percentile (median) net real return up until your retirement age. This is the expected return for the portfolio you currently hold modelled on current market conditions and adjusted for inflation.'}</li>
                <li className={styles.assumptionsListItem}>{'Once you reach your expected retirement age, the estimated income figure calculation uses an expected net return, which does not take account of inflation, therefore, inflation may impact upon the buying power of the estimated income.'}</li>
                <li className={styles.assumptionsListItem}>{'The return figure includes assumed charges of 0.82%p.a. to account for platform charges and ongoing advice fees, up until your retirement age. Fund charges will also have an impact on the value of your pension.'}</li>
                <li className={styles.assumptionsListItem}>{'Once you reach your expected retirement age, the estimated income figure calculation just uses fund charges. Assumed fees may be higher or lower than actual fees. After you reach your expected retirement age, fees will impact upon the value of your pension and the income you can take in retirement.'}</li>
                <li className={styles.assumptionsListItem}>{'Your final pension value and the income available will depend on a number of factors, including fund performance, contributions made, charges, inflation, your retirement age, the amount you withdraw from your pensions, tax and mortality rates.'}</li>
                <li className={styles.assumptionsListItem}>{'As with all investments, the value can fall and rise, therefore, you may get back less than you invest.'}</li>
                <li className={styles.assumptionsListItem}>{'Other charges may apply, which may affect the value of your pension and the amount of income available.'}</li>
                <li className={styles.assumptionsListItem}>{'The drawdown time period is calculated using your planned retirement age and life expectancy, based on the latest dataset published by the Office of National Statistics (Expectation of Life, 2022).'}</li>
                <li className={styles.assumptionsListItem}>{'The estimated income is based on an approximately 85% chance that your pension will not run out before you die.'}</li>
                <li className={styles.assumptionsListItem}>{'It is assumed that no lump sum withdrawals are taken from the pension, and the state pension is not included within the income calculation.'}</li>
                <li className={styles.assumptionsListItem}>{'The estimated income figure does not take into account tax charges which may apply to withdrawals or to contributions that exceed your allowances. Prevailing tax rates and reliefs are dependent on your individual circumstances and are subject to change.'}</li>
                <li className={styles.assumptionsListItem}>{'This calculation includes both regular personal and employer contributions where these have been set up. Both contribution types are increased in line with inflation. Personal contributions include the tax relief associated with a basic rate taxpayer. If you are a higher or additional rate taxpayer, you may be eligible for higher amounts of tax relief.'}</li>
                <li className={styles.assumptionsListItem}>{'If any of these assumptions do not apply to you, please contact us.'}</li>
                <li className={styles.assumptionsListItem}>
                  <span>
                    {'Before accessing your pension, it is important to consider all of your options. For more information on the government-backed free guidance service, visit'}
                  </span>
                  {' '}
                  <a className={styles.listItemLink} href="https://www.pensionwise.gov.uk" target="_blank" rel="noreferrer">{'pensionwise.gov.uk'}</a>
                </li>
              </ul>
            )}
          </div>
        </GlanceCardContent>
      </GlanceCard>
    </Modal>
  );
}

AssumptionsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  subHeadings: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      content: PropTypes.string,
    }),
  ),
};

AssumptionsModal.defaultProps = {
  subHeadings: null,
};

export default withPensionExpertTier(AssumptionsModal);
